import React, { useCallback, useEffect, useState } from 'react'
import context from '@/context'
import { ButtonGroupCustom, WhiteBox } from '@/components/Layout'
import { BsrsGrid, Column, commandColumnProps, DateCell, DateFilterCell, EnumCellStatus, EnumFilterCell } from '@/components/BsrsGrid'
import { reportSubmissionCheckingRuleStatus, reportSubmissionsStatus } from '@/components/reporting/submissions/reportSubmissionsStatus'
import LoadingIndicator from '@/components/LoadingIndicator'
import { ReportSubmissionState } from '@/enums/ReportSubmissionState'
import ErrorBoundary from '@/components/ErrorBoundary'
import { format } from '@/utils/StringUtilities'
import ReportSubmissionGroupNotes from './notes/detail'
import GroupHeader from './groupHeader'
import ReportDownloadButtons from '@/components/ReportDownloadButtons'
import ReportAttachments from '@/components/reporting/reportAttachments'
import ReportComments from '@/components/reporting/reportComments'
import { ReportSubmissionsTabs } from '@/components/reporting/submissions/reportSubmissionsTabs'
import { Button } from '@/components/button'
import { useLocalization } from '@/hooks/localization'
import { usePermissionChecker } from '@/hooks/usePermissionChecker'
import Resx from '@/components/Resx'
import { openConfirmDialog } from '@/utils/dialogHelpers'
import { useSignalR } from '@/hooks/useSignalR'
import { useDispatch } from '@/hooks/dispatch'
import { actionCreators } from '@/store/ReportSubmissionGroup'

function ReportSubmissions ({ forecasting, detailUrlFormat, addAttachments, reportSubmissionGroup, parentId, showInstitution, showRelatedLinks, previousSubmissionGroupState, removeAttachments }) {
  const { reportSubmissionGroupInternal, reportSubmissionGroup: group } = reportSubmissionGroup
  const { hasReportConstants } = reportSubmissionGroupInternal || {}
  const { ticketId } = group || {}
  const [showForceAcceptanceButton, setShowForceAcceptanceButton] = useState(false)

  const onRemoveAttachment = useCallback(id => removeAttachments({ entityId: parentId, fileIds: [id] }), [parentId, removeAttachments])
  const onForceGenerateClick = useCallback(() => openConfirmDialog({
    onAction: () => context.api.reportSubmissionGroupService.forceRegenerateExportFiles(parentId)
  }), [parentId])
  const onForceRecalculateClick = useCallback(() => openConfirmDialog({
    onAction: () => context.api.reportSubmissionGroupService.forceCellRecalculation(parentId)
  }), [parentId])
  const onSubmissionGroupReject = useCallback(() => openConfirmDialog({
    onAction: () => context.api.reportSubmissionGroupService.forceRejectGroup(parentId),
    customConfirmText: 'Reporting.ForceRejectGroup'
  }), [parentId])
  const onForceAcceptanceStartClick = useCallback(() => openConfirmDialog({
    onAction: () => {
      if (!parentId) return
      context.api.temporaryJobService.triggerReportSubmissionGroupsAcceptance(parentId)
    }
  }), [parentId])
  const { localize } = useLocalization()
  const isGranted = usePermissionChecker()

  const setShowForceAcceptance = useCallback(async () => {
    if (ticketId) {
      const b = await context.api.ticketService.getTicketStatus(ticketId)
      setShowForceAcceptanceButton(b && b.status === 2 && b.result === 8 && group && group.state === 2)
    }
  }, [group, ticketId])

  useEffect(() => {
    setShowForceAcceptance()
  }, [setShowForceAcceptance, group, ticketId])

  const [setGridKey] = useDispatch([actionCreators.setGridKey])
  useSignalR('checkingRulesFinished', async data => {
    const parsed = JSON.parse(data)
    const id = parseInt(parentId)
    if (parsed.reportInstanceGroupId === id) setGridKey()
  }, [])
  useSignalR('ReportSubmissionGroupAccepted', async data => {
    const parsed = JSON.parse(data)
    const id = parseInt(parentId)
    if (parsed.reportInstanceGroupId === id) setGridKey()
  }, [])

  const renderSheets = useCallback(() => {
    const enumName = 'ReportSubmissionState'
    const checkingRuleResultEnumName = 'CheckingRuleResult'

    return <WhiteBox
      header='Reporting.Sheets'
      buttons={<ErrorBoundary>
        <ReportDownloadButtons previousSubmissionGroupState={previousSubmissionGroupState} downloadXlsxPreviousVisible
          downloadXmlEmptyVisible downloadXmlPreviousVisible
          reportSubmissionGroup={reportSubmissionGroup.reportSubmissionGroup} />
      </ErrorBoundary>}
    >
      <BsrsGrid lastReloadedAt={reportSubmissionGroup.gridKey} defaultSort={[{ field: 'order', dir: 'asc' }]}
        dataSource={request => context.api.reportSubmissionService.getTableData(parentId, localize('Common.CBI'), forecasting, request)}>
        <Column field='order' title={localize('Common.Order')} filter='numeric' editable={false} />
        <Column field='name' title={localize('Common.ArabicName')} editable={false} />
        <Column field='latinName' title={localize('Common.LatinName')} editable={false} />
        <Column field='state' title={localize('Common.Status')} editable={false}
          cell={EnumCellStatus({ localize, enumName, visualStatus: reportSubmissionsStatus })}
          filterCell={EnumFilterCell({ localize, enumName }, ReportSubmissionState.values)} enumName={enumName} />
        <Column field='checkingRuleResult' title={localize('Reporting.CheckingRuleResult')} editable={false}
          cell={EnumCellStatus({
            localize,
            enumName: checkingRuleResultEnumName,
            visualStatus: reportSubmissionCheckingRuleStatus
          })}
          filterCell={EnumFilterCell({ localize, enumName: checkingRuleResultEnumName }, [0, 1, 2, 3, 4, 5, 6])}
          enumName={checkingRuleResultEnumName} />
        <Column field='lastStateModificationTime' title={localize('Common.StatusChangedAt')} filter='date'
          cell={DateCell} filterCell={DateFilterCell} editable={false} />
        <Column field='lastStateModifierUserName' title={localize('Common.StatusChangedBy')} editable={false} />
        <Column field='numberOfComments' title={localize('Reporting.NumberOfComments')} filter='numeric'
          editable={false} />
        <Column {...commandColumnProps({ detailUrl: x => format(detailUrlFormat, parentId, x.id) })} title=' '
          width={150} />
      </BsrsGrid>
    </WhiteBox>
  }, [detailUrlFormat, forecasting, localize, parentId, previousSubmissionGroupState, reportSubmissionGroup.gridKey, reportSubmissionGroup.reportSubmissionGroup])

  if (reportSubmissionGroup.fetching === true) {
    return <LoadingIndicator />
  }

  return (
    <ReportSubmissionsTabs parentId={parentId}>
      <ErrorBoundary><GroupHeader showInstitution={showInstitution} showRelatedLinks={showRelatedLinks}
        localize={localize}
        reportSubmissionGroup={reportSubmissionGroup.reportSubmissionGroup} /></ErrorBoundary>
      {isGranted('CanAccessManagementTools') && <WhiteBox header={'Reporting.ReportManagementTool'}>
        <ButtonGroupCustom>
          <Button onClick={onForceGenerateClick} icon='save'><Resx name='Reporting.RegenerateAllExports' /></Button>
          <Button onClick={onForceRecalculateClick} icon='calculator'><Resx
            name='Reporting.RecalculateAllCells' /></Button>
          <Button onClick={onSubmissionGroupReject} icon='x'><Resx name='Reporting.SubmissionGroupReject' /></Button>
          {showForceAcceptanceButton && <Button onClick={onForceAcceptanceStartClick}><Resx name='Reporting.ForceAcceptanceStart' /></Button>}
        </ButtonGroupCustom>
      </WhiteBox>}
      {renderSheets()}
      {hasReportConstants &&
      <ErrorBoundary>
        <WhiteBox header='Common.Constants' collapse>
          <BsrsGrid
            lastReloadedAt={reportSubmissionGroup.gridKey}
            dataSource={request => context.api.reportSubmissionGroupService.getUsedConstants(request, parentId)}
            defaultSort={[{ field: 'key', dir: 'asc' }]}>
            <Column field='key' title={localize('Common.Name')} editable={false} />
            <Column field='value' title={localize('Common.Value')} editable={false} />
            <Column field='description' title={localize('Common.Description')} editable={false} />
            <Column field='latinDescription' title={localize('Common.LatinDescription')} editable={false} />
            <Column field='validFrom' title={localize('Common.ValidFrom')} cell={DateCell}
              filterCell={DateFilterCell} filter='date' editable={false} />
            <Column field='validTo' title={localize('Common.ValidTo')} cell={DateCell} filterCell={DateFilterCell}
              filter='date' editable={false} />
          </BsrsGrid>
        </WhiteBox>
      </ErrorBoundary>}

      <ErrorBoundary>
        <ReportAttachments
          disabled
          key={reportSubmissionGroup.attachmentsKey}
          dataSource={() => context.api.reportSubmissionGroupService.getAttachments(parentId)}
          downloadAction={context.api.reportSubmissionGroupService.download}
          parentId={parentId}
          addAttachments={addAttachments}
          removeAttachment={onRemoveAttachment}
          adding={reportSubmissionGroup.adding}
          collapse
          collapsed
        />
      </ErrorBoundary>

      <ErrorBoundary>
        <ReportSubmissionGroupNotes parentId={parentId} collapse collapsed />
      </ErrorBoundary>

      <ErrorBoundary>
        <ReportComments hideHeader parentId={parentId} disabled={false} showExternalVisibilityCheckbox collapse collapsed />
      </ErrorBoundary>
    </ReportSubmissionsTabs>
  )
}

export {
  ReportSubmissions
}



// WEBPACK FOOTER //
// ./src/components/reporting/submissions/reportSubmissions.js